import React, { useState, useEffect, useMemo } from 'react';
import sidekick from '@last-rev/contentful-sidekick-util';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import indexOf from 'lodash/indexOf';
import keys from 'lodash/keys';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import { connect } from 'react-redux';
import useTranslation from 'next-translate/useTranslation';
import cx from 'clsx';
import styles from './PageCourseNavigation.module.scss';
import PageCourseTopicPropTypes from '../PageCourseTopic/PageCourseTopicPropTypes';
import ElementLink from '../ElementLink';
import Asset from '../Asset';
import TopicLink from '../TopicLink';

const mapStateToProps = (state) => ({
  completedCourseTopics: state.courses.completedCourseTopics,
  completedCourseTopicsLoaded: state.courses.loadedCourseTopics
});

export const PageCourseNavigationPropTypes = {
  courseId: PropTypes.string.isRequired,
  courseAs: PropTypes.string.isRequired,
  courseHref: PropTypes.string.isRequired,
  courseName: PropTypes.string.isRequired,
  topics: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape(PageCourseTopicPropTypes)])).isRequired,
  topic: PropTypes.shape(PageCourseTopicPropTypes).isRequired,
  progressClassName: PropTypes.string
};

const useTopicMarkers = (topics = [], topicId) => {
  const topicIdsHash = useMemo(() => map(topics, '_id').join('-'), [topics]);
  return useMemo(() => {
    if (!topics?.length) return null;
    const currentTopicIndex = indexOf(map(topics, '_id'), topicId);
    const previousTopic = !currentTopicIndex ? null : topics[currentTopicIndex - 1];
    const nextTopic = currentTopicIndex >= topics.length ? null : topics[currentTopicIndex + 1];

    return {
      currentTopicIndex,
      previousTopic,
      nextTopic
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicIdsHash, topicId]);
};

function PageCourseNavigation({
  topics,
  courseId,
  courseAs,
  courseHref,
  courseName,
  topic,
  progressClassName,
  // redux state
  completedCourseTopics,
  completedCourseTopicsLoaded
}) {
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  const onScroll = () => {
    const winScroll = document.documentElement.scrollTop;
    const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrolled = (winScroll / height) * 100;
    setScrollTop(scrolled);
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (topics && topics.length && completedCourseTopicsLoaded) {
      const courseTopicIds = map(topics, '_id') || [];
      const completedLength = intersection(keys(get(completedCourseTopics, courseId, {})), courseTopicIds).length;
      setPercentCompleted(Math.round(100 * (completedLength / topics.length)));
    }
  }, [courseId, topics, completedCourseTopicsLoaded, completedCourseTopics]);

  const { t } = useTranslation();
  const data = useTopicMarkers(topics, topic._id);

  let currentTopicIndex, previousTopic, nextTopic;

  if (data) {
    ({ currentTopicIndex, previousTopic, nextTopic } = data);
  } else {
    currentTopicIndex = -1; // or any default value you prefer
    previousTopic = null; // or a fallback value
    nextTopic = null; // or a fallback value
  }

  if (currentTopicIndex === -1) return null;

  return (
    <div data-testid="PageCourseNavigation" className={`pageCourseNavigation ${styles.pageCourseNavigation}`}>
      <div className={cx(styles.courseProgressWrap, progressClassName)}>
        <div className={styles.courseProgressBarWrap}>
          <div
            className={styles.progress}
            style={{ width: `${percentCompleted <= 0 || percentCompleted === 100 ? scrollTop : 100}%` }}>
            <div
              data-testid="PageCourseNavigation-progressBar"
              className={styles.progressBar}
              style={{ width: `${percentCompleted}%` }}
              role="progressbar"
              aria-valuenow={percentCompleted}
              aria-valuemin="0"
              aria-valuemax="100"
            />
          </div>
        </div>
      </div>

      <div className={styles.moduleContainerWrap}>
        <div className={styles.moduleContainerRow}>
          <div className={styles.previousTopicCol} data-testid="PageCourseNavigation-previousTopicWrap">
            {previousTopic ? (
              <TopicLink
                courseAs={courseAs}
                courseHref={courseHref}
                topic={previousTopic}
                className={styles.linkTopicNavigate}>
                <Asset
                  url="/images/assets/icon-chevron-left.svg"
                  title={t(`common:textPreviousTopic`)}
                  isInlineSvg
                  imageClassName={styles.iconNavigate}
                />
                <span
                  className={cx(styles.textTopicNavigate, styles.textTopicNavigatePrev)}
                  data-testid="PageCourseNavigation-previousTopicText">
                  {t(`common:textPreviousTopic`)}
                </span>
              </TopicLink>
            ) : (
              <ElementLink
                href={courseHref}
                as={courseAs}
                linkText={courseName}
                isModal={false}
                download={false}
                trackingId={`course_module_${courseHref}`}
                className={styles.linkTopicNavigate}>
                <Asset
                  url="/images/assets/icon-chevron-left.svg"
                  title={t(`common:textPreviousTopic`)}
                  isInlineSvg
                  imageClassName={styles.iconNavigate}
                />
                <span
                  className={cx(styles.textTopicNavigate, styles.textTopicNavigatePrev)}
                  data-testid="PageCourseNavigation-courseName">
                  {courseName}
                </span>
              </ElementLink>
            )}
          </div>

          <div
            className={styles.topicNameCol}
            {...sidekick(topic._id, 'name', null, 'Topic Name')}
            data-testid="PageCourseNavigation-topicName">
            {topic.name}
          </div>

          <div className={styles.nextTopicCol} data-testid="PageCourseNavigation-nextTopicWrap">
            {nextTopic ? (
              <TopicLink
                courseAs={courseAs}
                courseHref={courseHref}
                topic={nextTopic}
                className={styles.linkTopicNavigate}>
                <span
                  className={cx(styles.textTopicNavigate, styles.textTopicNavigateNext)}
                  data-testid="PageCourseNavigation-nextTopicText">
                  {t(`common:textNextTopic`)}
                </span>
                <Asset
                  url="/images/assets/icon-chevron-right.svg"
                  title={t(`common:textNextTopic`)}
                  isInlineSvg
                  imageClassName={styles.iconNavigate}
                />
              </TopicLink>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
PageCourseNavigation.propTypes = {
  ...PageCourseNavigationPropTypes,
  percentCompleted: PropTypes.number
};

PageCourseNavigation.defaultProps = {
  percentCompleted: 0,
  // eslint-disable-next-line react/default-props-match-prop-types
  progressClassName: 'theme-default'
};

export default connect(mapStateToProps)(PageCourseNavigation);
